<template>
    <div>
        <div class="d-flex justify-content-center">
            <div class="d-flex align-items-center" style="gap: 5px">
                <em
                    :class="{
                        down: !showDetails,
                        up: showDetails,
                    }"
                    class="icon-caret"
                />
                <a :class="{ disabled: isProcessing }" class="btn-link d-inline-block" href="javascript:void(0)" @click.stop="toggleDetails()">
                    <span
                        :style="{
                            visibility: isProcessing ? 'hidden' : 'visible',
                        }"
                    >
                        {{ trans('dashboard.asset_management.status.details.link') }}
                    </span>
                    <span
                        v-if="isProcessing"
                        class="position-absolute d-flex justify-content-center align-items-center"
                        style="top: 0; right: 0; bottom: 0; left: 0"
                    >
                        <spinner />
                    </span>
                </a>
            </div>
        </div>
        <collapse :show="showDetails">
            <header
                class="d-sm-flex justify-content-between align-items-center border-top border-color-primary bg-white px-3 py-2"
                style="min-height: 50px"
            >
                <div v-if="statusByFilter === 'location' && hasMultipleLocations" class="text-truncate mb-sm-0 mb-2">
                    {{ trans('dashboard.asset_management.status.details.group_by_location.title') }}
                </div>
                <div v-else class="text-truncate mb-sm-0 mb-2">
                    {{ trans('dashboard.asset_management.status.details.group_by_type.title') }}
                </div>
                <toggle-button
                    v-if="hasMultipleLocations"
                    v-model="statusByFilter"
                    :options="[
                        {
                            label: trans('dashboard.asset_management.status.details.toggle.location'),
                            value: 'location',
                        },
                        {
                            label: trans('dashboard.asset_management.status.details.toggle.asset_type'),
                            value: 'type',
                        },
                    ]"
                />
            </header>
            <section style="border-bottom-left-radius: 10px; border-bottom-right-radius: 10px; overflow: hidden">
                <div
                    v-for="(row, rowIndex) in details"
                    :key="row.id"
                    :class="{
                        'bg-gray-dark': rowIndex % 2 === 0,
                        'bg-white': rowIndex % 2 !== 0,
                    }"
                    class="px-3"
                >
                    <div :class="{ clickable: hasMultipleLocations }" @click.stop="toggleLocation(row)">
                        <row class="py-2">
                            <column :sm="true" xs="12">{{ row.name }}</column>
                            <column>
                                <operability-indicator size="sm" type="operable">
                                    <div>
                                        {{ row.operableCount || '-' }}&nbsp;/&nbsp;<span class="text-muted"
                                            >{{ getOperableCountPercentage(row) || '-' }} %</span
                                        >
                                    </div>
                                </operability-indicator>
                            </column>
                            <column>
                                <div class="d-flex justify-content-between align-items-center">
                                    <operability-indicator size="sm" type="inoperable">
                                        <div>
                                            {{ row.inoperableCount || '-' }}&nbsp;/&nbsp;
                                            <span class="text-muted">{{ getInoperableCountPercentage(row) || '-' }} %</span>
                                        </div>
                                    </operability-indicator>
                                    <em
                                        v-if="hasMultipleLocations"
                                        :class="{
                                            up: areLocationDetailsVisible(row),
                                            down: !areLocationDetailsVisible(row),
                                        }"
                                        class="icon-caret"
                                    />
                                </div>
                            </column>
                        </row>
                        <collapse v-if="row.childRows.length > 0" :show="areLocationDetailsVisible(row)">
                            <div v-for="(childRow, typeIndex) in row.childRows" :key="childRow.id">
                                <div class="border-top" />
                                <row class="py-2">
                                    <column :sm="true" xs="12">{{ childRow.name }} </column>
                                    <column>
                                        {{ childRow.operableCount || '-' }}
                                    </column>
                                    <column>
                                        {{ childRow.inoperableCount || '-' }}
                                    </column>
                                </row>
                            </div>
                        </collapse>
                    </div>
                </div>
            </section>
        </collapse>
    </div>
</template>
<script>
import OperabilityIndicator from '@/Components/OperabilityIndicator.vue';
import ToggleButton from '@/Components/ToggleButton.vue';
import JetButton from '@/Jetstream/Button.vue';

import Collapse from '@/Components/Collapse.vue';

export default {
    name: 'AssetStatusDetails',
    components: {
        Collapse,

        ToggleButton,
        JetButton,
        OperabilityIndicator,
    },
    data() {
        return {
            openLocationDetails: [],
            showDetails: false,
            isProcessing: false,
            statusByFilter: 'location',
        };
    },
    computed: {
        hasMultipleLocations() {
            return (this.$page.props.metrics?.groupedAssetStatusByLocation?.length ?? 0) > 1;
        },
        groupedAssetStatusByLocation() {
            return this.$page.props.metrics?.groupedAssetStatusByLocation || [];
        },
        details() {
            const data = this.groupedAssetStatusByLocation;

            // Early return for single location
            if (data.length === 1) {
                return this.transformSingleLocationData(data[0]);
            }

            // Return data based on filter type
            return this.statusByFilter === 'type' ? this.showDetailsGroupedByType(data) : this.showDetailsGroupedByLocation(data);
        },
        // Memoize the total counts for better performance
        totalCounts() {
            return this.details.reduce(
                (acc, row) => {
                    acc.operable += row.operableCount || 0;
                    acc.inoperable += row.inoperableCount || 0;
                    return acc;
                },
                { operable: 0, inoperable: 0 },
            );
        },
    },
    watch: {
        groupedAssetStatusByLocation: {
            handler(newValue) {
                if (newValue?.length === 1) {
                    this.openLocationDetails = [newValue[0].name];
                }
            },
            immediate: true,
        },
        statusByFilter() {
            this.openLocationDetails = [];
        },
    },
    methods: {
        // Transform data for single location view
        transformSingleLocationData(location) {
            return location.types.map((type) => ({
                id: type.type.value,
                name: type.type.shortLabel,
                operableCount: type.operableCount,
                inoperableCount: type.inoperableCount,
                childRows: [],
            }));
        },

        // Calculate percentages with safe division
        calculatePercentage(part, total) {
            return total > 0 ? Math.round((part / total) * 100) : 0;
        },

        getOperableCountPercentage(row) {
            const total = (row.operableCount || 0) + (row.inoperableCount || 0);
            return this.calculatePercentage(row.operableCount || 0, total);
        },

        getInoperableCountPercentage(row) {
            return 100 - this.getOperableCountPercentage(row);
        },

        toggleLocation(location) {
            if (this.groupedAssetStatusByLocation.length === 1) return;

            const index = this.openLocationDetails.indexOf(location.name);
            if (index === -1) {
                this.openLocationDetails.push(location.name);
            } else {
                this.openLocationDetails.splice(index, 1);
            }
        },

        areLocationDetailsVisible(location) {
            return this.openLocationDetails.includes(location.name);
        },

        async toggleDetails() {
            if (this.details.length > 0) {
                this.showDetails = !this.showDetails;
                return;
            }
            await this.loadDetails();
        },

        async loadDetails() {
            if (this.isProcessing) return;

            try {
                this.isProcessing = true;
                await this.$inertia.get(this.route('dashboard.index'), this.route().params, {
                    preserveState: true,
                    only: ['groupedAssetStatusByLocation'],
                    onSuccess: () => {
                        this.showDetails = true;
                    },
                });
            } finally {
                this.isProcessing = false;
            }
        },

        showDetailsGroupedByLocation(data) {
            return data.map((location) => ({
                id: `${location.name} (${location.iataCode})`,
                name: `${location.name} (${location.iataCode})`,
                operableCount: location.totalOperableCount,
                inoperableCount: location.totalInoperableCount,
                childRows: this.transformTypes(location.types),
            }));
        },

        transformTypes(types) {
            return types.map((type) => ({
                id: type.type.value,
                name: type.type.shortLabel,
                operableCount: type.operableCount,
                inoperableCount: type.inoperableCount,
            }));
        },

        showDetailsGroupedByType(data) {
            const typeLocationMap = this.createTypeLocationMap(data);
            return this.transformTypeLocationMap(typeLocationMap);
        },

        createTypeLocationMap(data) {
            const typeLocationMap = {};

            data.forEach((location) => {
                location.types.forEach((type) => {
                    const typeValue = type.type.value;
                    if (!typeLocationMap[typeValue]) {
                        typeLocationMap[typeValue] = {
                            value: typeValue,
                            name: type.type.shortLabel,
                            locations: [],
                        };
                    }

                    typeLocationMap[typeValue].locations.push({
                        id: `${location.name} (${location.iataCode})`,
                        name: `${location.name} (${location.iataCode})`,
                        inoperableCount: type.inoperableCount,
                        operableCount: type.operableCount,
                    });
                });
            });

            return typeLocationMap;
        },

        transformTypeLocationMap(typeLocationMap) {
            return Object.values(typeLocationMap)
                .map(({ value, name, locations }) => {
                    locations.sort((a, b) => b.inoperableCount - a.inoperableCount);

                    const totals = locations.reduce(
                        (acc, loc) => ({
                            operable: acc.operable + (loc.operableCount || 0),
                            inoperable: acc.inoperable + (loc.inoperableCount || 0),
                        }),
                        { operable: 0, inoperable: 0 },
                    );

                    return {
                        id: value,
                        name,
                        operableCount: totals.operable,
                        inoperableCount: totals.inoperable,
                        childRows: locations,
                    };
                })
                .sort((a, b) => {
                    const maxInoperableA = Math.max(...a.childRows.map((loc) => loc.inoperableCount || 0));
                    const maxInoperableB = Math.max(...b.childRows.map((loc) => loc.inoperableCount || 0));
                    return maxInoperableB - maxInoperableA;
                });
        },
    },
};
</script>
